/**
 * Pure helpers for the INCOMING_REFERENCE attribute editor. Kept free of React so
 * the option-building and selection-matching logic can be unit-tested directly
 * (see incomingReferenceAttributeEditor.utils.test.ts).
 */

/** A raw candidate row: an OBJECT_TEMPLATE_REFERENCE attribute on some other
 * template whose `reference_object_template_id` is the template being edited. */
export interface IncomingReferenceCandidateRow {
    id: string;
    name?: string | null;
    object_template_id?: string | null;
    object_template_id_object_template?: { name?: string | null } | null;
}

export interface IncomingReferenceOption {
    /** The candidate attribute's id — the stable <option value>. */
    value: string;
    /** Display label: `<referencing object template name>.<attribute name>`. */
    label: string;
    /** The attribute name stored on this attribute's `incoming_reference_attribute`. */
    attributeName: string;
    /** The referencing object_template id, stored on `reference_object_template_id`. */
    objectTemplateId: string;
}

/**
 * Map raw candidate rows into picker options labelled
 * `<referencing object template name>.<attribute name>`. Rows missing an id, a
 * name, or an owning template id are dropped — they cannot settle both config
 * values, so they must not be offered.
 */
export const buildIncomingReferenceOptions = (
    candidates: Array<IncomingReferenceCandidateRow | null | undefined> | null | undefined
): IncomingReferenceOption[] =>
    (candidates ?? [])
        .filter(
            (row): row is IncomingReferenceCandidateRow =>
                !!row && !!row.id && !!row.name && !!row.object_template_id
        )
        .map((row) => ({
            value: row.id,
            attributeName: row.name as string,
            objectTemplateId: row.object_template_id as string,
            label: `${row.object_template_id_object_template?.name ?? row.object_template_id}.${
                row.name
            }`,
        }));

/**
 * Resolve which option corresponds to the stored config
 * (incoming_reference_attribute + reference_object_template_id). Matched on BOTH
 * values because an attribute name alone is not unique across templates.
 */
export const findSelectedIncomingReferenceOption = (
    options: IncomingReferenceOption[],
    incomingReferenceAttribute: string | null | undefined,
    referenceObjectTemplateId: string | null | undefined
): IncomingReferenceOption | undefined => {
    if (!incomingReferenceAttribute || !referenceObjectTemplateId) {
        return undefined;
    }
    return options.find(
        (option) =>
            option.attributeName === incomingReferenceAttribute &&
            option.objectTemplateId === referenceObjectTemplateId
    );
};
